import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getUroSDK, listUnifiedJobs } from '../../../services/URO';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import PHTile from '../../../components/app-shell/PHTile';
import { catColors } from '../../../components/app-shell/catColors';
import useClientData from '../../../hooks/useClientData';
import useResponsive from '../../../utils/useResponsive';
import {
  ImageIcon, VideoIcon, ChatIcon, WorkflowIcon, LayersIcon,
  SparkIcon, ArrowIcon,
} from '../../../components/app-shell/icons';

/* ─── static card definitions (exact from design) ─────────────── */

const BIG_CARDS = [
  { l: 'Generate an image', d: 'From Flux, GPT Image, and more',  Icon: ImageIcon,    from: 'rgba(255,126,103,.45)', to: 'rgba(255,77,141,.18)',  ring: 'rgba(255,126,103,.35)', label: '#FFC2B5', path: '/zen' },
  { l: 'Make a video',      d: 'From Kling, Veo 3, Luma',         Icon: VideoIcon,    from: 'rgba(255,77,141,.45)',  to: 'rgba(167,139,250,.18)', ring: 'rgba(255,77,141,.40)',  label: '#FFB1C8', path: '/zen' },
  { l: 'Chat with an LLM', d: 'GPT-4.1, Gemini, and more',        Icon: ChatIcon,     from: 'rgba(99,102,241,.45)', to: 'rgba(129,140,248,.18)', ring: 'rgba(129,140,248,.35)', label: '#A5B4FC', path: '/llm-playground' },
  { l: 'Run a workflow',   d: 'Multi-step pipelines',              Icon: WorkflowIcon, from: 'rgba(167,139,250,.45)',to: 'rgba(99,102,241,.18)',  ring: 'rgba(167,139,250,.35)', label: '#C4B5FD', path: '/zen' },
];

function getModelCategory(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('video')) return 'Video';
  if (t.includes('text') || t.includes('llm')) return 'Text';
  if (t.includes('workflow')) return 'Workflow';
  return 'Image';
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const m = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ─── main ─────────────────────────────────────────────────────── */

export default function DashboardScreen() {
  const navigate = useNavigate();
  const { balance, clientName, clientEmail } = useClientData();
  const { isMobile } = useResponsive();

  const [trendingModels, setTrendingModels] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const hour = new Date().getHours();
  const greeting = `Good ${hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'}, ${clientName.split(' ')[0]}`;

  useEffect(() => {
    (async () => {
      try {
        const res = await getUroSDK().listModels({ limit: 8, zen_enabled: 1 });
        const data = res?.data;
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : Array.isArray(data?.data) ? data.data : [];
        setTrendingModels(items.slice(0, 7));
      } catch {}
      setLoadingModels(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await listUnifiedJobs({ limit: 6, status: 'completed' });
        setRecentJobs((res?.data?.items || []).slice(0, 6));
      } catch {}
      setLoadingJobs(false);
    })();
  }, []);

  /* recent generations: 3-col desktop, 2-col mobile — count drives both the
     JS array split and the grid template, single source of truth */
  const recentColCount = isMobile ? 2 : 3;
  const cols = Array.from({ length: recentColCount }, () => []);
  recentJobs.forEach((j, i) => cols[i % recentColCount].push(j));

  return (
    <AppScreenWrapper>
      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={balance} name={clientName} email={clientEmail} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <ScreenOrbs tone="orange" />
          <Topbar greeting={greeting} name={clientName} />

          <div className="scroll-y app-screen-content" style={{ flex: 1, padding: '28px 32px 40px', position: 'relative', zIndex: 1 }}>

            {/* Section 1 — Create cards (exact from design) */}
            <section>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
                <h2 className="dash-hero-title" style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em' }}>What do you want to create today?</h2>
              </div>
              <div className="dash-create-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 16 }}>
                {BIG_CARDS.map((c, i) => (
                  <a key={i} href="#" onClick={e => { e.preventDefault(); navigate(c.path); }} className="card dash-create-card" style={{
                    padding: 22, height: 200, position: 'relative', overflow: 'hidden',
                    textDecoration: 'none', color: '#fff',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  }}>
                    <div style={{ position: 'absolute', inset: '-20% -20% auto auto', height: '70%', width: '70%', background: `radial-gradient(circle at 70% 30%, ${c.from}, transparent 65%)`, filter: 'blur(8px)', pointerEvents: 'none' }} />
                    <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', position: 'relative', background: 'rgba(255,255,255,.05)', border: `1px solid ${c.ring}`, color: c.label }}>
                      <c.Icon s={20} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>{c.l}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>{c.d}</div>
                    </div>
                  </a>
                ))}
              </div>
            </section>

            {/* Section 2 — Trending models (exact from design) */}
            <section style={{ marginTop: 40 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Trending models this week</h2>
                <a href="#" onClick={e => { e.preventDefault(); navigate('/zen'); }} style={{ fontSize: 13, color: 'var(--text-3)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Explore all 50+ <ArrowIcon s={12} />
                </a>
              </div>
              <div className="no-scrollbar" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
                {loadingModels
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="card" style={{ flex: '0 0 230px', height: 130 }} />
                    ))
                  : trendingModels.map((m, i) => {
                      const cat = getModelCategory(m.model_type);
                      const c = catColors(cat);
                      const price = m.lowest_cost != null ? `from $${Number(m.lowest_cost).toPrecision(2)}` : null;
                      return (
                        <div key={m.model_id} className="card" style={{ flex: '0 0 230px', padding: 10, cursor: 'pointer' }}
                          onClick={() => navigate(`/zen/model/${m.model_id}`)}
                        >
                          {m.model_thumbnail_image_url || m.model_cover_image_url
                            ? <img src={m.model_thumbnail_image_url || m.model_cover_image_url} alt={m.model_name} style={{ width: '100%', borderRadius: 8, display: 'block', aspectRatio: '16/10', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                            : <PHTile label="sample" kind={cat === 'Video' ? 'video' : 'image'} from={c.from} to={c.to} ratio={16/10} />
                          }
                          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{m.model_name}</div>
                            <span className="chip" style={{ color: c.label, borderColor: c.ring, height: 20, fontSize: 10 }}>{cat}</span>
                          </div>
                          {price && (
                            <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
                              <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{price}</span>
                              <span style={{ fontSize: 11, color: c.label, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Try now <ArrowIcon s={10} /></span>
                            </div>
                          )}
                        </div>
                      );
                    })}
              </div>
            </section>

            {/* Section 3 — Recent generations (exact from design) */}
            <section style={{ marginTop: 40 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Your recent generations</h2>
                <a href="#" onClick={e => { e.preventDefault(); navigate('/gallery'); }} style={{ fontSize: 13, color: 'var(--text-3)', textDecoration: 'none' }}>
                  View all in Gallery <ArrowIcon s={12} />
                </a>
              </div>
              {loadingJobs ? (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${recentColCount}, minmax(0,1fr))`, gap: 14 }}>
                  {Array.from({ length: recentColCount }).map((_, i) => <div key={i} className="card" style={{ height: 160 }} />)}
                </div>
              ) : recentJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-3)', fontSize: 13 }}>
                  No generations yet — create something in Zen
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${recentColCount}, minmax(0,1fr))`, gap: 14 }}>
                  {cols.map((col, ci) => (
                    <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {col.map(job => {
                        const isWorkflow = job.job_type === 'workflow';
                        const results = isWorkflow
                          ? (Array.isArray(job.results?.results) ? job.results.results : Array.isArray(job.results) ? job.results : [])
                          : (Array.isArray(job.results) ? job.results : []);
                        const thumb = results[0]?.external_url || results[0]?.url || results[0]?.content;
                        const modelName = isWorkflow ? (job.workflow_definition_name || 'Workflow') : (job.model_name || 'Model');
                        return (
                          <div key={job.image_job_id || job.workflow_job_id} className="card" style={{ padding: 10, cursor: 'pointer' }}
                            onClick={() => navigate('/gallery')}
                          >
                            {(() => {
                              if (!thumb) return <div style={{ width: '100%', aspectRatio: '16/10', borderRadius: 10, background: 'rgba(255,255,255,.04)', display: 'grid', placeItems: 'center', fontSize: 18, color: 'var(--text-4)' }}>✦</div>;
                              const isVideo = results[0]?.content_type === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(thumb);
                              return isVideo
                                ? <video src={thumb} muted loop playsInline style={{ width: '100%', borderRadius: 10, display: 'block', aspectRatio: '16/10', objectFit: 'cover' }} onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
                                : <img src={thumb} alt={modelName} style={{ width: '100%', borderRadius: 10, display: 'block', aspectRatio: '16/10', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />;
                            })()}
                            <div className="row" style={{ justifyContent: 'space-between', marginTop: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{modelName}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{getTimeAgo(job.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Section 4 — Quick actions (exact from design) */}
            <section style={{ marginTop: 40 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 18 }}>Quick actions</h2>
              <div className="dash-quick-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
                {/* Continue session */}
                <div className="card" style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/zen')}>
                  <div style={{ width: 56, height: 56, borderRadius: 12, flex: '0 0 56px', background: 'linear-gradient(135deg, rgba(255,126,103,.4), rgba(255,77,141,.2))', border: '1px solid rgba(255,126,103,.35)', display: 'grid', placeItems: 'center' }}>
                    <SparkIcon s={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 4 }}>Continue session</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Open Zen workspace</div>
                    <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 2 }}>Images, videos, workflows</div>
                  </div>
                  <ArrowIcon s={14} />
                </div>

                {/* Explore */}
                <a href="#" onClick={e => { e.preventDefault(); navigate('/zen'); }} className="card row" style={{ padding: 18, gap: 14, textDecoration: 'none', color: '#fff', cursor: 'pointer' }}>
                  <div style={{ width: 56, height: 56, borderRadius: 12, flex: '0 0 56px', background: 'linear-gradient(135deg, rgba(99,102,241,.4), rgba(167,139,250,.2))', border: '1px solid rgba(129,140,248,.35)', display: 'grid', placeItems: 'center', color: '#A5B4FC' }}>
                    <LayersIcon s={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 4 }}>Explore all models</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Browse the full catalog</div>
                    <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 2 }}>50+ models · Image, Video, Text, Workflow</div>
                  </div>
                  <span className="chip" style={{ color: '#A5B4FC', borderColor: 'rgba(129,140,248,.3)' }}>50+</span>
                </a>
              </div>
            </section>

          </div>
        </div>
      </div>
    </AppScreenWrapper>
  );
}
